
import { defineComponent } from "vue";
import AnchorButton from "@/components/AnchorButton.vue";
import FeatureSection from "@/components/FeatureSection.vue";
import TestimonialCard from "@/components/TestimonialCard.vue";
import amplitude from "amplitude-js";
import firebase from "firebase/app";

interface Feature {
  uncolored: string;
  colored: string;
  description: string;
  img: string;
  alt: string;
  color: string;
}

const FEATURES: Feature[] = [
  {
    uncolored: "Feedback,",
    colored: "no guesswork",
    description:
      "Time-coded comments enable you and your collaborators to pinpoint exactly where the track needs work. The days of making the right changes to the wrong spots are gone.",
    img: "/features/time-coded-comments.png",
    alt: "Audio waveform with comments at specific spots.",
    color: "#FFD056"
  },
  {
    uncolored: "Your bounces",
    colored: "available anywhere",
    description:
      "From studio to subway, your music is within reach. Your collaborators can listen to tracks without ever needing to create an account or download an app.",
    img: "/features/imessage-sharing.png",
    alt: "Text message showing Samply link being shared",
    color: "#F54F9F"
  },
  {
    uncolored: "Iterate",
    colored: "without frustration",
    description:
      "Each track you upload is automatically versioned so that you never need to think about file names or folder structure. You can rest easy knowing your clients are listening to the right version.",
    img: "/features/organization-grid.png",
    alt: "Messy files next to clean organized versions",
    color: "#23C7E1"
  }
];

interface Testimonial {
  name: string;
  quote: string;
  credentials: string;
  img: string;
  link: string;
  color: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    name: "Aaron Sterling",
    quote:
      "Samply is a complete game changer for me!  When it comes to sending out mixes to clients for feedback, my entire workflow has been dramatically improved. Every feature I had wished for in an app like this is actually here!",
    credentials: "Drummer, Producer - John Mayer, Taylor Swift, Harry Styles",
    link: "https://aaronsterling.com/",
    color: "#FFD056",
    img: "/testimonial_photos/aaron.jpg"
  },
  {
    name: "Sidney “Ømen” Brown",
    quote:
      "Samply definitely is a new wave of file sharing, I rock with the interactive approach and simplicity of the platform.",
    credentials: "Producer - Drake, Beyoncé, Lil Wayne, Ludacris",
    link: "https://solo.to/omn",
    color: "#23C7E1",
    img: "/testimonial_photos/omen.jpg"
  },
  {
    name: "Justin Perkins",
    quote:
      "I’ve been wanting a solution like Samply for a long time now to help with mastering approvals. Most of my clients are on mobile devices when their master comes in and they want to listen to it ASAP. Samply's gapless and lossless playback engine allows my clients to experience the true flow of an album accurately and conveniently.",
    credentials:
      "Mastering Engineer - Busta Rhymes, En Vogue, The Replacements",
    link: "https://www.mysteryroommastering.com",
    color: "#EE7E16",
    img: "/testimonial_photos/justin.jpg"
  },
  {
    name: "Wendy Wang",
    quote:
      "I've been using Samply at different stages of the music-making process.  From writing, to production, to mixing.  It's easy to make notes at specific timestamps along the audio.  It has been especially helpful when I switch from song-to-song or project-to-project to have pertinent information all organized.",
    credentials:
      "Producer, Songwriter - the bird & the bee, Tori Kelly, Foo Fighters",
    link: "http://www.thesweethurt.com/about",
    color: "#F54F9F",
    img: "/testimonial_photos/wendy.jpg"
  },
  {
    name: "Dan “Chimy” Chmielinski",
    quote:
      "Samply is every music collaborator's dream. The days of sending countless links back and forth, making wrong changes to the wrong spots and struggling to make things work from afar are over. Samply's sleek and intuitive design makes it the logical one stop shop that will change the game forever.",
    credentials:
      "Bassist, Film Composer - Wynton Marsalis, Joey Alexander, Marc Cary",
    link: "https://www.chimyonthebass.com/bio",
    color: "#BB6BD9",
    img: "/testimonial_photos/dan.jpg"
  }
];

const VIDEO_URL =
  "https://fast.wistia.net/embed/iframe/bwukauqbvy?videoFoam=true";

export default defineComponent({
  name: "Home",
  setup() {
    // Analytics
    const deviceId = amplitude.getInstance().options.deviceId;
    const APP_URL =
      process.env.VUE_APP_ENDPOINTS === "production"
        ? `https://samply.app/?deviceid=${deviceId}`
        : `https://staging.samply.app/?deviceid=${deviceId}`;
    // UI
    const gutters = [
      "mx-auto",
      "xl:max-w-5xl",
      "lg:max-w-4xl",
      "px-4",
      "sm:px-8",
      "lg:px-10"
    ];
    // Extract remote configuartion values
    const config = firebase.remoteConfig();
    const landingPage = JSON.parse(config.getString("landing_page_copy"));
    const {
      header,
      subheader,
      secondary_header: secondaryHeader,
      primary_cta: primaryCTA,
      secondary_cta: secondaryCTA
    } = landingPage;
    return {
      appURL: APP_URL,
      gutters,
      video: VIDEO_URL,
      testimonials: TESTIMONIALS,
      features: FEATURES,
      header,
      subheader,
      secondaryHeader,
      primaryCTA,
      secondaryCTA
    };
  },
  components: {
    AnchorButton,
    FeatureSection,
    TestimonialCard
  },
  mounted() {
    const observer = new IntersectionObserver(
      entries => {
        // If intersectionRatio is 0, the target is out of view
        // and we do not need to do anything.
        for (const entry of entries) {
          const visible = entry.intersectionRatio > 0;
          if (visible) {
            const target = entry.target as HTMLElement;
            const visibility = entry.intersectionRatio;
            const scaledVisibility = Math.pow(
              1 - Math.log10(10 - 9 * visibility),
              1.5
            );
            target.style.opacity = scaledVisibility.toString();
          }
        }
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
        rootMargin: "-72px 0px 0px 0px"
      }
    );
    // Observe features
    for (let i = 1; i <= this.features.length; i += 1) {
      const el = document.querySelector(`#feature-${i}`) as HTMLElement;
      el.style.opacity = "0";
      observer.observe(el);
    }
  },
  methods: {
    onCTA(buttonText: string) {
      amplitude.getInstance().logEvent("[Website] Clicked CTA", { buttonText });
    }
  }
});
